import CheckIcon from '@heroicons/react/20/solid/CheckIcon.js';
import React from 'react';
import {
  Header as AriaHeader,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuSection as AriaMenuSection,
  MenuTrigger as AriaMenuTrigger,
  OverlayTriggerStateContext,
  Popover as AriaPopover,
  type Selection,
  Separator as AriaSeparator,
  Text as AriaText,
} from 'react-aria-components';

import { tv } from '../../tv';
import { ButtonSpinner } from '../button/button';
import { OverlayPortal, surfacedOverlay, useOverlayPresence } from '../overlay/overlay-portal';
import { OVERLAY_OFFSET, RAC_PLACEMENT } from '../overlay/placement';
import { rowStyles } from '../overlay/row';
import { UnavailableReason } from '../overlay/unavailable-reason';

/**
 * ComplyKit Menu: a list of actions behind one trigger.
 *
 * The first SURFACED overlay (overlays.md): the pointer and focus enter it,
 * so it renders as a surface among surfaces (surface-overlay fill, a
 * border-subtle edge, the one overlay shadow), where the ephemeral Tooltip
 * renders as the inverted pill. It rides the whole overlay foundation: the
 * overlay z layer through the inline style slot, the portal convention, the
 * shared motion pair with the placement-aware shift.
 *
 * THE TRIGGER-PROP RULE. A component that decorates exactly one child takes
 * it AS the child (Tooltip wraps its trigger); a component whose children are
 * a collection names its trigger as a prop, because the collection owns the
 * children slot. Menu is the first of the second kind: `children` is the
 * items and separators, `trigger` is the one ComplyKit interactive element
 * that opens it. The trigger is seated through React Aria's trigger context,
 * so a plain `<IconButton />` opens the menu with no extra props, shows
 * pressed styling while the menu is open, and keeps its built-in instant
 * label tooltip.
 *
 * The popup is content-sized above a 180px floor, never the trigger's width
 * (matching the trigger is Select's contract, not a menu's), and caps at the
 * available viewport height with internal scroll.
 *
 * Selection lives in MenuSection: a section with a selectionMode carries its
 * own checked state, so one menu can mix action items with option sections.
 *
 * NOT here by design: submenus and context-menu triggers (menu.md).
 */
export interface MenuProps {
  /**
   * The one ComplyKit interactive element that opens the menu, usually an
   * IconButton. Named as a prop because `children` carries the collection
   * (the trigger-prop rule); seated via context, so it needs no wiring, shows
   * pressed styling while the menu is open, and an IconButton keeps its
   * instant label tooltip.
   */
  trigger: React.ReactElement;
  /**
   * Where the popup sits relative to the trigger. When there is no room on
   * the asked-for side it flips to the opposite one.
   */
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end';
  /** Whether the menu is open (controlled). */
  isOpen?: boolean;
  /** Whether the menu is open when first rendered (uncontrolled). */
  defaultOpen?: boolean;
  /** Handler called when the menu opens or closes. */
  onOpenChange?: (isOpen: boolean) => void;
  /** The menu's contents: MenuItem, MenuSection and MenuSeparator elements only. */
  children: React.ReactNode;
}

/* The surfaced-overlay recipe (overlays.md): surface-overlay fill, a
 * border-subtle edge, the one overlay shadow, and the shared motion pair with
 * the axis picked by the placement the popup actually resolved to. The popup
 * is content-sized above the 180px floor and React Aria caps its max-height
 * to the available space inline, so overflow-auto is what turns a long menu
 * into an internal scroll. The popup and the section geometry are the menu's
 * own; the items are the shared list row (overlay/row.ts), so a picker's
 * options match them. */
const menuStyles = tv({
  slots: {
    popup: [
      ...surfacedOverlay,
      // Auto width shrinks against the positioned left edge, feeding back into collision updates.
      'ck:w-max ck:max-w-full ck:min-w-45 ck:overflow-auto',
    ],
    separator: 'ck:my-1 ck:border-t ck:border-border-divider',
    /* The section box itself has no look: the popup's padding and the
     * separator between sections are the geometry, decided once above. */
    section: '',
  },
});

/* The selection-layer mirror of the press-layer suppression: a suppressed
 * item stays RAC-ENABLED (the unavailable form needs hover and arrow focus),
 * so the section's GroupSelectionManager would still toggle it on press.
 * Suppressed items register their keys here so the section's controlled
 * bridge can drop those toggles before they reach the consumer. */
const SectionSuppressionContext = React.createContext<{
  register: (key: string) => () => void;
  has: (key: string) => boolean;
} | null>(null);

interface MenuItemCommonProps {
  /** The item's label. A string on purpose: a menu item is an action's name,
   * and it doubles as the typeahead text. */
  children: string;
  /**
   * The item's stable key. Required inside a selection section, where it is
   * the value the section's `selectedKey`/`selectedKeys` name, and must be
   * unique across the whole menu (the sections share the menu's one
   * collection). Elsewhere the collection keys itself.
   */
  id?: string;
  /**
   * One plain sentence of secondary context, rendered as a muted second line
   * under the label; anything longer belongs elsewhere.
   */
  description?: string;
  /**
   * Glyph rendered before the label: a heroicons 24/outline component, or a
   * custom SVG drawn in currentColor. The item sizes and colours it from its
   * own recipe (icon.md); it inherits the item's colour, tone included.
   */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Recolours the item (label and glyph) onto the red/error scale for
   * destructive actions, with the hover wash moving to the red alpha steps.
   * The same one-value union as Button, and it grows only on evidence.
   */
  tone?: 'critical';
  /**
   * Whether the item is disabled. Alone, this is the real disabled form:
   * washed, skipped by arrow keys, unreachable. With `disabledReason` it
   * becomes the UNAVAILABLE form instead: still reachable by hover and arrow
   * keys so the reason can be read, announced as disabled, with the action
   * (or navigation) fully suppressed and the menu staying open on press.
   */
  isDisabled?: boolean;
  /**
   * Why the item cannot be used right now. Presence switches a disabled item
   * to the unavailable form (see `isDisabled`) and shows this text as a
   * tooltip on hover and on keyboard focus. Inside a selection section the
   * unavailable option also never changes selection. One plain sentence,
   * the Tooltip contract.
   */
  disabledReason?: string;
  /**
   * Whether selecting the item closes the menu. Turn off for actions the
   * user repeats from the same menu. Unavailable and pending items keep the
   * menu open regardless.
   */
  closesOnSelect?: boolean;
}

/* Somewhere to go OR something to do, the Button union: a navigation cannot
 * carry an in-flight state, and an action cannot carry a target, so the
 * invalid combinations do not compile instead of being silently ignored. */
interface MenuItemLinkProps extends MenuItemCommonProps {
  /**
   * Where the item goes. Renders a real link that rides the RouterProvider
   * bridge (button.md), so it client-navigates, can be opened in a new tab
   * and copied. Navigation takes `href`; an in-page action takes `onAction`.
   */
  href: string;
  /** Link target, for the rare item that opens away from the app. */
  target?: '_blank';
  onAction?: never;
  isPending?: never;
}

interface MenuItemActionProps extends MenuItemCommonProps {
  /** What the item does: an action on the page rather than a navigation. */
  onAction?: () => void;
  /**
   * The in-flight state of the item's own action: a trailing spinner appears,
   * presses are ignored (selection included, inside a selection section), the
   * menu stays open, and the item announces itself as busy.
   */
  isPending?: boolean;
  href?: never;
  target?: never;
}

export type MenuItemProps = MenuItemLinkProps | MenuItemActionProps;

/**
 * One action in a Menu.
 *
 * Carries the Button vocabulary at menu scale: the onAction/href union, a
 * `critical` tone that recolours without reshaping, a pending state, an
 * optional `description` second line, and the unavailable-item mechanism
 * (isDisabled + disabledReason) that keeps a disabled action reachable so
 * the reason it is disabled stays readable.
 */
export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(function MenuItem(
  {
    children,
    id,
    description,
    icon: IconGlyph,
    tone,
    isDisabled = false,
    disabledReason,
    closesOnSelect = true,
    onAction,
    isPending = false,
    href,
    target,
  },
  forwardedRef,
) {
  // A navigation has no in-flight state; the union already forbids it.
  const pending = isPending && !href;

  /* THE UNAVAILABLE FORM (the Spectrum pattern). A really-disabled item fires
   * no interactions, so the commonest disabled-menu-item idiom, explaining
   * WHY, cannot work on it. With a reason present the item therefore stays
   * ENABLED for React Aria (hover and arrow keys reach it), while this
   * component suppresses everything the interaction could do: onAction and
   * href are dropped entirely, the menu stays open on press, the washed look
   * is restated by the recipe, aria-disabled is announced, and the reason
   * renders as the inverted tooltip pill on hover and on arrow-key focus. */
  const unavailable = isDisabled && disabledReason != null;
  const suppressed = unavailable || pending;

  /* Inside a selection section a suppressed press would still toggle
   * selection (RAC selects on press before onAction is consulted), so the
   * key is registered for the section's bridge to ignore. This effect runs
   * in the hidden collection pass, the one place this wrapper renders. */
  const suppression = React.useContext(SectionSuppressionContext);
  React.useEffect(() => {
    if (!suppressed || id == null || suppression == null) return;
    return suppression.register(id);
  }, [suppressed, id, suppression]);

  const {
    item,
    itemIcon,
    itemCheck,
    itemLabel,
    itemText,
    itemDescription,
    spinnerSeat,
    spinnerDisc,
  } = rowStyles({
    tone: tone ?? 'neutral',
    unavailable,
    pending,
  });

  /* The item element, for the reason tooltip's anchor and the aria
   * attributes below; merged with the forwarded ref.
   *
   * PROVEN CAVEAT (menu.md): aria-disabled does NOT pass through React
   * Aria's filterDOMProps in the installed 1.19.0 (its global set admits only
   * id, data-*, dir/lang/hidden/inert/translate and the global events), and
   * useMenuItem derives aria-disabled solely from its own isDisabled, which
   * the unavailable form deliberately does not set. aria-busy is filtered the
   * same way. Both attributes therefore ride the ref callback, which is also
   * the only hook-shaped thing guaranteed to see the REAL item element:
   * this wrapper's render (and its effects) runs inside React Aria's hidden
   * collection pass, before the visible element exists, while the collected
   * ref is attached to the rendered item itself. The callback's identity
   * carries the states, so React re-invokes it whenever they change. React
   * never renders aria-disabled while the item is unavailable, so nothing
   * fights the imperative write; when the item is really disabled React Aria
   * owns the attribute and this callback leaves it alone. */
  const itemRef = React.useRef<HTMLDivElement | null>(null);
  const composedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      itemRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
      if (!node) return;
      if (unavailable) node.setAttribute('aria-disabled', 'true');
      else if (!isDisabled) node.removeAttribute('aria-disabled');
      if (pending) node.setAttribute('aria-busy', 'true');
      else node.removeAttribute('aria-busy');
    },
    [forwardedRef, unavailable, isDisabled, pending],
  );

  return (
    <AriaMenuItem
      ref={composedRef}
      id={id}
      textValue={children}
      isDisabled={unavailable ? undefined : isDisabled}
      onAction={suppressed ? undefined : onAction}
      href={unavailable ? undefined : href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      shouldCloseOnSelect={suppressed ? false : closesOnSelect}
      className={item()}
      data-component="menu-item"
    >
      {({ isHovered, isFocused, isSelected, selectionMode }) => (
        <>
          {/* The checkmark column exists only inside a selection section:
           * React Aria's render state carries the section's mode, so a plain
           * action menu never grows the column. The checked state itself is
           * announced through aria-checked; the glyph is presentation. */}
          {selectionMode !== 'none' && (
            <CheckIcon
              aria-hidden="true"
              focusable="false"
              className={itemCheck({ selected: isSelected })}
            />
          )}
          {IconGlyph && <IconGlyph aria-hidden="true" focusable="false" className={itemIcon()} />}
          {description == null ? (
            <span className={itemLabel()}>{children}</span>
          ) : (
            /* The native Text slots: useMenuItem hands label/description ids
             * through TextContext, and useSlotId keeps aria-labelledby and
             * aria-describedby on the item only while the slots are mounted,
             * so the accessible name stays the label alone. */
            <div className={itemText()}>
              <AriaText slot="label">{children}</AriaText>
              <AriaText slot="description" className={itemDescription()}>
                {description}
              </AriaText>
            </div>
          )}
          {pending && <ButtonSpinner overlay={spinnerSeat()} disc={spinnerDisc()} />}
          {unavailable && (
            <UnavailableReason itemRef={itemRef} isOpen={isHovered || isFocused}>
              {disabledReason}
            </UnavailableReason>
          )}
        </>
      )}
    </AriaMenuItem>
  );
});

interface MenuSectionCommonProps {
  /**
   * The section's heading, rendered as the muted label above the options and
   * announced as the group's accessible name. React Aria keeps it out of the
   * arrow-key order.
   */
  title: string;
  /** The section's options: MenuItem elements, each with an `id`. */
  children: React.ReactNode;
}

/* One choice OR several, a typed union like the item's onAction/href: the
 * single form holds exactly one key (so a sort seat can never be sorted by
 * nothing), the multiple form holds an array, and mixing the two prop shapes
 * does not compile. */
interface MenuSectionSingleProps extends MenuSectionCommonProps {
  /**
   * How the section selects: `'single'` keeps exactly one checked option per
   * section, the radio form (`menuitemradio`), while `'multiple'` allows any
   * number, the checkbox form (`menuitemcheckbox`).
   */
  selectionMode: 'single';
  /** The id of the checked item. Never empty: pressing the checked option
   * again keeps it checked rather than clearing the section. */
  selectedKey: string;
  /** Handler called with the newly checked item's id. */
  onSelectionChange: (key: string) => void;
  selectedKeys?: never;
}

interface MenuSectionMultipleProps extends MenuSectionCommonProps {
  /**
   * How the section selects: `'single'` keeps exactly one checked option per
   * section, the radio form (`menuitemradio`), while `'multiple'` allows any
   * number, the checkbox form (`menuitemcheckbox`).
   */
  selectionMode: 'multiple';
  /** The ids of the checked items. */
  selectedKeys: string[];
  /** Handler called with the full set of checked ids after a toggle. */
  onSelectionChange: (keys: string[]) => void;
  selectedKey?: never;
}

export type MenuSectionProps = MenuSectionSingleProps | MenuSectionMultipleProps;

/**
 * A selection section inside a Menu: options rather than actions.
 *
 * Rides React Aria's native section-level selection: a MenuSection with a
 * `selectionMode` gets its own selection manager (checked state local to the
 * section, focus still the menu's, so arrow keys sweep sections and action
 * items as one list), its items take the `menuitemradio`/`menuitemcheckbox`
 * role with `aria-checked`, and the checkmark column renders from the item's
 * own render state. Keys are the items' `id`s and must be unique across the
 * whole menu, not just the section: the managers all read the menu's one
 * collection.
 *
 * Always controlled: an option section reflects state the caller owns (a sort
 * order, a filter), never state a popup should keep for itself. Whether a
 * press closes the menu stays where it already lives, the item's
 * `closesOnSelect`: a filter section that should stay open while the user
 * builds a selection turns it off per item.
 *
 * A suppressed option (unavailable or pending) never changes the section's
 * selection: the press is dropped before `onSelectionChange`, and
 * `aria-checked` stays where it was.
 */
export const MenuSection = React.forwardRef<HTMLElement, MenuSectionProps>(
  function MenuSection(props, ref) {
    const { section } = menuStyles();
    const { sectionHeading } = rowStyles();
    const single = props.selectionMode === 'single';

    const suppressedKeys = React.useRef(new Set<string>());
    const suppression = React.useMemo(
      () => ({
        register: (key: string) => {
          suppressedKeys.current.add(key);
          return () => {
            suppressedKeys.current.delete(key);
          };
        },
        has: (key: string) => suppressedKeys.current.has(key),
      }),
      [],
    );

    /* React Aria hands the new selection as 'all' | Set<Key>: 'all' has no
     * menu-shaped producer and the guard only narrows the type. The single
     * form converts set to key; disallowEmptySelection makes the empty set
     * unreachable, so a missing first key means nothing changed.
     *
     * Suppressed keys never move selection: the manager selects on press
     * before the item's suppression is consulted, so the bridge is where the
     * toggle is dropped, and the controlled props snap RAC's state back. In
     * single mode the old key's removal is only a consequence of the new
     * key's arrival, so the added keys alone decide. */
    const onSelectionChange = (selection: Selection) => {
      if (selection === 'all') return;
      const keys = Array.from(selection, String);
      const currentKeys =
        props.selectionMode === 'single' ? [props.selectedKey] : props.selectedKeys;
      const currentSet = new Set(currentKeys);
      const incoming = new Set(keys);
      const toggled = single
        ? keys.filter((key) => !currentSet.has(key))
        : [
            ...keys.filter((key) => !currentSet.has(key)),
            ...currentKeys.filter((key) => !incoming.has(key)),
          ];
      if (toggled.length > 0 && toggled.every((key) => suppression.has(key))) return;
      if (props.selectionMode === 'single') {
        if (keys[0] != null) props.onSelectionChange(keys[0]);
      } else {
        props.onSelectionChange(keys.filter((key) => currentSet.has(key) || !suppression.has(key)));
      }
    };

    return (
      <AriaMenuSection
        ref={ref}
        selectionMode={props.selectionMode}
        selectedKeys={single ? [props.selectedKey] : props.selectedKeys}
        disallowEmptySelection={single}
        onSelectionChange={onSelectionChange}
        className={section()}
        data-component="menu-section"
      >
        <AriaHeader className={sectionHeading()} data-component="menu-section-heading">
          {props.title}
        </AriaHeader>
        <SectionSuppressionContext.Provider value={suppression}>
          {props.children}
        </SectionSuppressionContext.Provider>
      </AriaMenuSection>
    );
  },
);

/** A hairline between groups of items. No props: one rule, decided once. */
export const MenuSeparator = React.forwardRef<HTMLElement, Record<string, never>>(
  function MenuSeparator(_props, ref) {
    return (
      <AriaSeparator
        ref={ref}
        className={menuStyles().separator()}
        data-component="menu-separator"
      />
    );
  },
);

const MenuPopup = React.forwardRef<HTMLDivElement, Pick<MenuProps, 'placement' | 'children'>>(
  function MenuPopup({ placement = 'bottom-start', children }, forwardedRef) {
    const state = React.useContext(OverlayTriggerStateContext);
    const { popup } = menuStyles();
    const { list } = rowStyles();
    const { popupRef, isPresent } = useOverlayPresence(state?.isOpen, forwardedRef);
    if (!isPresent) return null;

    return (
      <OverlayPortal name="menu">
        <AriaPopover
          ref={popupRef}
          placement={RAC_PLACEMENT[placement]}
          offset={OVERLAY_OFFSET}
          className={popup()}
          style={{ zIndex: 'var(--ck-z-overlay)' } as React.CSSProperties}
          data-component="menu"
        >
          <AriaMenu className={list()}>{children}</AriaMenu>
        </AriaPopover>
      </OverlayPortal>
    );
  },
);

export const Menu = React.forwardRef<HTMLDivElement, MenuProps>(function Menu(
  { trigger, placement = 'bottom-start', isOpen, defaultOpen, onOpenChange, children },
  ref,
) {
  return (
    <AriaMenuTrigger isOpen={isOpen} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger}
      <MenuPopup ref={ref} placement={placement}>
        {children}
      </MenuPopup>
    </AriaMenuTrigger>
  );
});
